import { useApolloClient, useMutation } from "@apollo/client";
import {
  Alert,
  Heading,
  NumericFormatInput,
  PrimaryFullScreenSpinner,
  toast,
  useAppForm,
} from "@repo/ui";
import { NotFoundError } from "@repo/utils";
import { createFileRoute, useParams } from "@tanstack/react-router";
import Decimal from "decimal.js";
import { Suspense } from "react";
import { z } from "zod";
import { graphql } from "~/gql/client";
import { currencyValues } from "~/gql/client/enumValues";
import { opsHead } from "~/head";

export const Route = createFileRoute(
  "/__auth/ledger/transactions_/$transactionId/entries/new",
)({
  head: () => opsHead("New Ledger Entry"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const formSchema = z
  .object({
    amount: z
      .number()
      .min(0.01, "Amount must be greater than 0")
      .transform((v) => new Decimal(v)),
    unit: z.string().min(1, "Unit is required"),
    creditLedgerAccountId: z
      .string()
      .min(1, "Credit ledger account is required"),
    debitLedgerAccountId: z.string().min(1, "Debit ledger account is required"),
  })
  .refine((data) => data.creditLedgerAccountId !== data.debitLedgerAccountId, {
    message: "Credit and debit accounts must be different",
    path: ["debitLedgerAccountId"],
  });

function View() {
  const { transactionId } = useParams({ strict: false });
  if (!transactionId) {
    throw new NotFoundError();
  }

  const client = useApolloClient();

  const [createLedgerEntries, { loading, error }] = useMutation(
    graphql(`
      mutation CreateLedgerEntries($input: CreateLedgerEntriesInput!) {
        createLedgerEntries(input: $input) {
          transaction {
            id
          }
        }
      }
    `),
  );

  const form = useAppForm({
    defaultValues: {
      amount: 0,
      unit: "AUD",
      creditLedgerAccountId: "",
      debitLedgerAccountId: "",
    },
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: ({ value }) => {
      const parsed = formSchema.safeParse(value);
      if (!parsed.success) {
        toast({
          kind: "error",
          title: "Error creating ledger entry",
        });
        return;
      }
      const formValues = parsed.data;

      createLedgerEntries({
        variables: {
          input: {
            transactionId,
            amount: formValues.amount,
            unit: formValues.unit,
            creditLedgerAccountId: formValues.creditLedgerAccountId,
            debitLedgerAccountId: formValues.debitLedgerAccountId,
          },
        },
        onCompleted: () => {
          client.cache.evict({
            id: `LedgerTransaction:${transactionId}`,
          });
          client.cache.gc();
          form.reset();
        },
      });
    },
  });

  return (
    <div className="mt-4 rounded-md border p-6">
      <div className="flex flex-col gap-4">
        <Heading type="page-title" as="span" className="mb-2">
          Add ledger entries
        </Heading>

        <Alert
          title="Danger"
          kind="warning"
          description="This form does not validate the ownership of the ledger accounts, use with caution."
        />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
          className="grid w-full max-w-lg items-start gap-6"
        >
          <form.AppForm>
            <form.AppField name="amount">
              {(field) => (
                <div>
                  <Heading as="span" type="field" className="pb-2">
                    Amount
                  </Heading>
                  <NumericFormatInput
                    value={field.state.value}
                    onChange={(v) =>
                      v.target.value &&
                      field.handleChange(v.target.value.toNumber())
                    }
                    required
                    thousandSeparator=","
                    decimalScale={2}
                    fixedDecimalScale
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="mt-1 text-red-600 text-sm">
                      {field.state.meta.errors
                        .map((err) => err?.message)
                        .join(", ")}
                    </p>
                  )}
                </div>
              )}
            </form.AppField>

            <form.AppField name="unit">
              {(field) => (
                <field.SelectField
                  label="Unit"
                  placeholder="Select unit..."
                  errorVisibilityMode="onBlur"
                >
                  {currencyValues.map((currency) => (
                    <field.SelectItem key={currency} value={currency}>
                      {currency}
                    </field.SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>

            <form.AppField name="creditLedgerAccountId">
              {(field) => (
                <field.InputField
                  label="Credit Ledger Account ID"
                  placeholder="Enter credit account ID..."
                  errorVisibilityMode="onBlur"
                />
              )}
            </form.AppField>

            <form.AppField name="debitLedgerAccountId">
              {(field) => (
                <field.InputField
                  label="Debit Ledger Account ID"
                  placeholder="Enter debit account ID..."
                  errorVisibilityMode="onBlur"
                />
              )}
            </form.AppField>

            {error && (
              <Alert title="Error" kind="error" description={error.message} />
            )}

            <div className="mt-6 flex w-full justify-end gap-2">
              <form.SubmitButton
                childrenWhileSubmitting="Creating entries..."
                disabled={loading}
              >
                Create entries
              </form.SubmitButton>
            </div>
          </form.AppForm>
        </form>
      </div>
    </div>
  );
}
